import React, { Suspense } from 'react';
import { defer, useLoaderData, Await } from "react-router-dom";
import LoadingScreen from './LoadingScreen';
import {createLinkFormatter, createNumberFormatter} from './TableUtils.js';

import 'react-tabulator/lib/styles.css';
import 'react-tabulator/lib/css/tabulator.min.css';
import { ReactTabulator } from 'react-tabulator'

const _cache = new Map();

const fetchAll = async (rawTransactionRecordId) => {
  const recordUrl = (process.env.NODE_ENV === 'development' ? '/raw_transaction_record.json' : '/raw_transaction_records') + '?raw_transaction_record_id=' + rawTransactionRecordId;
  const propsUrl = (process.env.NODE_ENV === 'development' ? '/raw_transaction_record_properties.json' : '/raw_transaction_record_properties') + '?raw_transaction_record_id=' + rawTransactionRecordId;

  let txnPromise;
  if (process.env.NODE_ENV === 'development') {
    txnPromise = fetch('/transactions.json').then(r => r.json())
      .then(res => res.data.filter(t => String(t.id).startsWith(rawTransactionRecordId + '|')));
  } else {
    const txnParams = new URLSearchParams({
      'filter[0][field]': 'id',
      'filter[0][type]': 'starts',
      'filter[0][value]': rawTransactionRecordId + '|',
      'page': '1',
      'size': '100',
    });
    txnPromise = fetch('/transactions?' + txnParams.toString()).then(r => r.json())
      .then(res => res.data || []);
  }

  const [record, properties, transactions] = await Promise.all([
    fetch(recordUrl).then(r => r.json()).catch(e => { console.error(e); return []; }),
    fetch(propsUrl).then(r => r.json()).catch(e => { console.error(e); return []; }),
    txnPromise,
  ]);

  const result = { record, properties, transactions };
  _cache.set(rawTransactionRecordId, result);
  return result;
};

export const loader = async ({ params }) => {
  const rawTransactionRecordId = params.rawTransactionRecordId;
  if (_cache.has(rawTransactionRecordId)) {
    return { all: _cache.get(rawTransactionRecordId) };
  }
  return defer({ all: fetchAll(rawTransactionRecordId) });
};

function RawTransactionDetail() {
  const loaderData = useLoaderData();

  return (
    <Suspense fallback={<LoadingScreen visible="true" />}>
      <Await resolve={loaderData.all}
             errorElement={<div>Error Loading</div>}>
        {({ record: records, properties, transactions }) => {
          const record = records[0] || {};
          const recordData = [
            { key: "raw_transaction_record_id", value: record.raw_transaction_record_id },
            { key: "day", value: record.day },
            { key: "create_process", value: record.create_process },
            { key: "raw_file_record_id", value: record.raw_file_record_id },
            { key: "create_date", value: record.create_date },
            { key: "update_date", value: record.update_date },
          ];

          return (
            <div className="raw-transaction-detail">
              <h3>Raw Transaction Record</h3>
              <ReactTabulator
                options={{
                  data: recordData,
                }}
                columns={[
                  { title: "key", field: "key", responsive: 0, cssClass: "ws-pre" },
                  { title: "value", field: "value", responsive: 0, cssClass: "ws-pre",
                    formatter: (cell) => {
                      const row = cell.getRow().getData();
                      const value = cell.getValue();
                      if (value == null || value === '') return '';
                      if (row.key === "create_process") {
                        const a = document.createElement("a");
                        a.textContent = value;
                        a.href = `#/parsing/${value}`;
                        return a;
                      }
                      if (row.key === "raw_file_record_id") {
                        const a = document.createElement("a");
                        a.textContent = value;
                        a.href = `#/file/${value}`;
                        return a;
                      }
                      return String(value);
                    },
                  },
                ]}
              />
              <h3>Properties</h3>
              <ReactTabulator
                options={{
                  data: properties,
                  initialSort: [
                    {column: "key", dir: "asc"},
                  ],
                }}
                columns={[
                  { title: "key", field: "key", responsive: 0, cssClass: "ws-pre" },
                  { title: "value", field: "value", responsive: 0, cssClass: "ws-pre" },
                ]}
              />
              {transactions.length > 0 && (
                <>
                  <h3>Translated Transactions ({transactions.length})</h3>
                  <ReactTabulator
                    options={{
                      data: transactions,
                      layout: "fitDataStretch",
                    }}
                    columns={[
                      { title: "day", field: "day" },
                      { title: "account", field: "account",
                        formatter: createLinkFormatter((cell) => `/transactions?account=${cell.getValue()}`), },
                      { title: "symbol", field: "symbol",
                        formatter: createLinkFormatter((cell) => `/transactions?symbol=${cell.getValue()}`), },
                      { title: "quantity", field: "quantity", formatter: createNumberFormatter(2), hozAlign: 'right' },
                      { title: "other_account", field: "other_account" },
                      { title: "other_symbol", field: "other_symbol" },
                      { title: "other_quantity", field: "other_quantity", formatter: createNumberFormatter(2), hozAlign: 'right' },
                      { title: "type", field: "type" },
                      { title: "note", field: "note" },
                    ]}
                  />
                </>
              )}
            </div>
          );
        }}
      </Await>
    </Suspense>
  );
}

export default RawTransactionDetail;
